import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { client } from '../../api/client';
import user from '../../../profile/user';
import postcodeFormatting from '../../../utils/postcode-formatting';

export const Status = Object.freeze({
	Uninitialised: 'uninitialised',
	Loading: 'loading',
	Succeeded: 'succeeded',
	Failed: 'failed'
});

export const SearchType = Object.freeze({
	Used: 'Used',
	New: 'New',
	NearlyNew: 'NearlyNew'
});

const initialState = {
	filterStatus: Status.Uninitialised,
	filterState: {
		searchType: SearchType.Used,
		resultsUrl: null,
		manufacturerItems: [],
		navigatedManufacturer: null,
		rangeItems: [],
		navigatedRange: null,
		modelItems: [],
		navigatedModel: null,
		navigatedPrice: null
	},
	location: {
		invalid: false,
		postcode: postcodeFormatting.format(user.getPostcode() ?? '')
	},
};

const resetDependentApiDataFunctions = {
	manufacturer: (apiData) => {
		apiData.range = null;
		apiData.model = null;
	},

	range: (apiData) => {
		apiData.model = null;
	}
};

const makeApiData = (state, filterName, filterValue) => {
	const filterState = state.filterState;

	// Reset taxonomy filter values if search type changes, as they may not be valid selected values
	if (filterName === 'searchType') {
		return {
			searchType: filterValue,
			postcode: state.location.postcode
		};
	}

	const result = {
		searchType: filterState.searchType,
		manufacturer: filterState.navigatedManufacturer?.value,
		range: filterState.navigatedRange?.value,
		model: filterState.navigatedModel?.value,
		price: filterState.navigatedPrice?.value,
		postcode: state.location.postcode
	};

	if (filterName) {
		result[filterName] = filterValue;
	}

	if (!filterValue) {
		const resetDependentDataFunction = resetDependentApiDataFunctions[filterName];

		if (resetDependentDataFunction) {
			resetDependentDataFunction(result);
		}
	}

	return result;
};

export const fetchUpdatedFilterState = createAsyncThunk(
	'homeForSale/fetchUpdatedFilterState',
	async (filter, { getState }) => {
		const { homeForSale } = getState();

		const apiData = makeApiData(homeForSale, filter.name, filter.value);

		const response = await client.post(
			'/api/cfs-search/filters/',
			apiData
		);

		return {
			filterState: response.data
		};
	}
);

export const fetchInitialState = createAsyncThunk(
	'homeForSale/fetchInitialState',
	async (initialState) => {
		const response = await client.get(
			'/api/cfs-search/initial-filters/' + initialState
		);

		return {
			filterState: response.data
		};
	},
	{
		condition: (initialState, { getState }) => {
			const { homeForSale } = getState();

			if (homeForSale.filterStatus !== Status.Uninitialised) {
				return false;
			}
		}
	}
);

export const homeForSaleSlice = createSlice({
	name: 'homeForSale',
	initialState,
	reducers: {
		updatePostcode(state, action) {
			state.location.postcode = action.payload;
			const isValid = postcodeFormatting.isValid(action.payload);
			state.location.invalid = !isValid;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchUpdatedFilterState.pending, (state) => {
				state.filterStatus = Status.Loading;
			})
			.addCase(fetchUpdatedFilterState.fulfilled, (state, action) => {
				const { filterState } = action.payload;
				state.filterStatus = Status.Succeeded;
				state.filterState = filterState;
			})
			.addCase(fetchUpdatedFilterState.rejected, (state) => {
				state.filterStatus = Status.Failed;
			});

		builder
			.addCase(fetchInitialState.pending, (state) => {
				state.filterStatus = Status.Loading;
			})
			.addCase(fetchInitialState.fulfilled, (state, action) => {
				const { filterState } = action.payload;
				state.filterStatus = Status.Succeeded;
				state.filterState = filterState;
			})
			.addCase(fetchInitialState.rejected, (state) => {
				state.filterStatus = Status.Failed;
			});
	}
});

export const { updatePostcode } = homeForSaleSlice.actions;

export default homeForSaleSlice.reducer;